import { format, isValid } from 'date-fns';
import type { IRatingMatrixFrameworkGradeScore, IRatingMatrixFrameworkParameter } from '__generated__';
import { APP_FORMAT_DATE, APP_FORMAT_DATE_TIME } from 'configs/const';
import { weightFractionToPercent } from 'services/rating-matrix-framework-parameter';
import type {
  IFullTemplate,
  IValidateWeightsResult,
  SignOffDateTimeParts,
  TemplatePatchableFields,
} from './models';

export function getDefaultCloneVersionCode(): string {
  return format(new Date(), 'ddMMyy');
}

export function formatEditorDate(date: unknown): string {
  if (!date) {
    return '';
  }

  const parsed = new Date(date as string);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return format(parsed, APP_FORMAT_DATE);
}

export function formatEditorUpdatedDate(date: unknown): string {
  if (!date) {
    return '';
  }

  const parsed = new Date(date as string);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return format(parsed, APP_FORMAT_DATE_TIME);
}

export function getTemplateVersionMetaParts(template: IFullTemplate) {
  return {
    version: template.version ?? String(template.id),
    created: formatEditorDate(template.createdDate),
    user: template.updatedByName ?? '',
    updated: formatEditorUpdatedDate(template.updatedDate),
  };
}

export function getScaleSummaryParts(gradeScores: IRatingMatrixFrameworkGradeScore[]) {
  if (!gradeScores.length) {
    return null;
  }

  const sorted = [...gradeScores].sort((left, right) => left.numericScore - right.numericScore);
  const worst = sorted[sorted.length - 1]?.gradeLabel ?? '';
  const best = sorted[0]?.gradeLabel ?? '';

  if (!worst && !best) {
    return null;
  }

  return {
    worst,
    best,
    count: gradeScores.length,
  };
}

export function getScoreRangeParts(min: number | null | undefined, max: number | null | undefined) {
  if (min == null || max == null || Number.isNaN(min) || Number.isNaN(max)) {
    return null;
  }

  return {
    min: String(min),
    max: String(max),
  };
}

export function computeTotalWeightPercent(parameters: IRatingMatrixFrameworkParameter[]): number {
  if (!parameters.length) {
    return 0;
  }

  const total = parameters.reduce((sum, parameter) => sum + (parameter.weight ?? 0), 0);
  return weightFractionToPercent(total);
}

const PATCHABLE_KEYS: (keyof TemplatePatchableFields)[] = [
  'title',
  'version',
  'isSubSheet',
  'lockedByUserID',
  'lockedDate',
  'approvedByUserID',
  'approvedDate',
  'scoreRangeMin',
  'scoreRangeMax',
  'notchStep',
  'maxNotchAdjustment',
];

function normalizeDateValue(value: unknown): string | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value as string);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString();
}

function fieldChanged(
  key: keyof TemplatePatchableFields,
  server: TemplatePatchableFields,
  draft: TemplatePatchableFields,
): boolean {
  const left = server[key];
  const right = draft[key];

  if (key === 'lockedDate' || key === 'approvedDate') {
    return normalizeDateValue(left) !== normalizeDateValue(right);
  }

  return left !== right;
}

export function buildTemplatePatch(
  server: TemplatePatchableFields,
  draft: TemplatePatchableFields,
): Partial<TemplatePatchableFields> {
  const patch: Partial<TemplatePatchableFields> = {};

  PATCHABLE_KEYS.forEach((key) => {
    if (fieldChanged(key, server, draft)) {
      patch[key] = draft[key] as never;
    }
  });

  return patch;
}

export function pickPatchableFields(template: IFullTemplate): TemplatePatchableFields {
  return {
    title: template.title,
    version: template.version,
    isSubSheet: template.isSubSheet,
    lockedByUserID: template.lockedByUserID,
    lockedDate: template.lockedDate,
    approvedByUserID: template.approvedByUserID,
    approvedDate: template.approvedDate,
    scoreRangeMin: template.scoreRangeMin,
    scoreRangeMax: template.scoreRangeMax,
    notchStep: template.notchStep,
    maxNotchAdjustment: template.maxNotchAdjustment,
  };
}

export function isTemplateDraftDirty(
  server: TemplatePatchableFields,
  draft: TemplatePatchableFields,
): boolean {
  return Object.keys(buildTemplatePatch(server, draft)).length > 0;
}

export function parseSignOffDateTime(value: unknown): SignOffDateTimeParts {
  if (!value) {
    return { date: null, time: '' };
  }

  const parsed = new Date(value as string);
  if (!isValid(parsed)) {
    return { date: null, time: '' };
  }

  return {
    date: parsed,
    time: format(parsed, 'HH:mm'),
  };
}

export function combineSignOffDateTime(parts: SignOffDateTimeParts): Date | null {
  if (!parts.date) {
    return null;
  }

  const [hours = '0', minutes = '0'] = parts.time.split(':');
  const combined = new Date(parts.date);
  combined.setHours(Number(hours) || 0, Number(minutes) || 0, 0, 0);

  return combined;
}

export function formatLockDetailsMeta(template: IFullTemplate): string | null {
  if (!template.lockedByUserID && !template.lockedDate) {
    return null;
  }

  const parsedLockDate = template.lockedDate ? new Date(template.lockedDate as string) : null;
  const datePart = parsedLockDate && isValid(parsedLockDate) ? formatEditorDate(template.lockedDate) : '';
  const timePart = parsedLockDate && isValid(parsedLockDate) ? format(parsedLockDate, 'HH:mm') : '';
  const userPart = template.lockedByName ?? '';

  if (userPart && datePart) {
    return `${userPart} · ${datePart}${timePart ? ` ${timePart}` : ''}`;
  }

  return userPart || datePart || null;
}

export type LockPreflightCheck = {
  id: string;
  labelKey: string;
  passed: boolean;
  issueDescription?: string;
};

export function buildLockPreflightChecks(
  template: IFullTemplate,
  weightsResult?: IValidateWeightsResult | null,
): LockPreflightCheck[] {
  const parameters = template.parameters ?? [];
  const gradeScores = template.gradeScores ?? [];
  const hasSubsForAllParams =
    parameters.length === 0 ||
    parameters.every((parameter) => {
      const subs = (parameter as { subParameters?: unknown[] }).subParameters;
      if (subs === undefined) {
        return true;
      }
      return subs.length > 0;
    });
  const scoreRangeValid =
    template.scoreRangeMin != null &&
    template.scoreRangeMax != null &&
    template.scoreRangeMin < template.scoreRangeMax;
  const scaleDefined = gradeScores.length > 0;
  const lockedBySelected = Boolean(template.lockedByUserID && template.lockedDate);
  const approvedBySelected = Boolean(template.approvedByUserID && template.approvedDate);
  const weightsValid = weightsResult?.isValid ?? false;

  return [
    {
      id: 'weights',
      labelKey: 'editor-dlg-lock-check-weights',
      passed: weightsValid,
    },
    {
      id: 'subs-per-parameter',
      labelKey: 'editor-dlg-lock-check-subs',
      passed: hasSubsForAllParams,
    },
    {
      id: 'scale-defined',
      labelKey: 'editor-dlg-lock-check-scale',
      passed: scaleDefined,
    },
    {
      id: 'score-range',
      labelKey: 'editor-dlg-lock-check-score-range',
      passed: scoreRangeValid,
    },
    {
      id: 'locked-by-signer',
      labelKey: 'editor-dlg-lock-check-locked-by',
      passed: lockedBySelected,
    },
    {
      id: 'approved-by-signer',
      labelKey: 'editor-dlg-lock-check-approved-by',
      passed: approvedBySelected,
    },
  ];
}

/** F-27 — fallback when the Content-Disposition header is not exposed to the browser (CORS). */
export function buildTemplateExcelFileName(templateId: number, date: Date) {
  return `rating-matrix-template-${templateId}-${format(date, 'yyyyMMdd')}.xlsx`;
}
